// import AdminDivisionsCamera from '@/data/mapping-camera-comuni7917.json';

import AdminDivisionsCamera from '@/data/mapping-camera-comuni7917-solo-ABRUZZO.json';

import {
  getCoalitionColor,
  getFullNameCandidate,
  getMostraDettaglio,
  getName,
  getPartyColor,
} from '@/lib/candidatesHelpers';
import { useMainContext } from '@/lib/context';
import { convertIdComune, convertIdProvince } from '@/lib/convertId';
import { formatNumber, parsePercentage } from '@/lib/helpers';
import { getLevelSettings } from '@/lib/levelsHelpers';
import {
  AutocompleteOption,
  DataLevel,
  DynamicTurnout,
  Region,
  Result,
  ResultAPI,
  ResultCodes,
  Year,
} from '@/lib/model';
import { formatDecimal } from '@/utils/format-decimal.util';
import {
  getAffluenzaByLivelloEleT,
  getAffluenzaByLivelloPerc,
  getAffluenzaByLivelloVoti,
} from './affluenzaHelper';

const API_URL = 'https://2024-real-time-data.s3.eu-central-1.amazonaws.com/'; // Config.API_URL;

let fetchController = new AbortController();
let fetchDynamicTurnoutController = new AbortController();

export const parseResult = (
  result: ResultAPI,
  year: Year,
  region: Region,
  level: DataLevel,
  dynamicTurnout: DynamicTurnout[]
) => {
  return {
    level,
    year,
    region,
    placeName:
      (level === DataLevel.MAIN || level === DataLevel.REGIONE
        ? result.int.desc_reg
        : level === DataLevel.PROVINCIA
        ? result.int.desc_prov
        : result.int.desc_com) || '',
    showVoti: true,
    scrutinized: {
      current: result.int.sz_perv || result.int.sz_pres || 0,
      total: result.int.sz_tot || 0,
    },
    coalitions: result.cand
      ?.map(coalition => ({
        coalitionId: coalition.pos,
        label: getName(year, region, true, coalition),
        fullName: getFullNameCandidate(
          year,
          region,
          true,
          coalition,
          result.isMultiCollegio
        ),
        votes: formatNumber(coalition.voti_uni || coalition.voti || 0),
        percentage: parsePercentage(
          coalition.perc_uni || coalition.perc || '0'
        ),
        fillColor: (function () {
          let color = getCoalitionColor(year, region, level, coalition);
          return color;
        })(),
        candidates: getMostraDettaglio(year, region, coalition.pos)
          ? coalition.liste
              ?.map(candidate => ({
                coalitionId: coalition.pos,
                candidateId: candidate.pos,
                label: getName(year, region, false, candidate), // change to this when in PROD -> list.desc_lis,
                fullName: getFullNameCandidate(
                  year,
                  region,
                  false,
                  candidate,
                  result.isMultiCollegio
                ),
                votes: formatNumber(candidate.voti),
                percentage: parsePercentage(candidate.perc),
                fillColor: getPartyColor(year, region, candidate.pos),
              }))
              .sort(({ percentage: a }, { percentage: b }) => b - a)
          : [],
      }))
      .sort(({ percentage: a }, { percentage: b }) => b - a),
    idCodes: {
      codReg: `${result.int.cod_reg}`,
      codCirc: `${result.int.cod_circ}`,
      codProv: `${result.int.cod_prov}`,
      codClPlu: `${result.int.cod_plu}`,
      codClUni: `${result.int.cod_cl_uni}`,
      codCom: `${result.int.cod_com}`,
    },
    summary: {
      turnout: {
        label: 'Affluenza',
        value:
          Year.Y2024 === year
            ? `${formatDecimal(
                getAffluenzaByLivelloPerc(
                  dynamicTurnout,
                  level,
                  result.int.cod_reg,
                  result.int.cod_prov,
                  result.int.cod_com
                ),
                true
              )}`
            : `${formatDecimal(parsePercentage(result.int.perc_vot), true)}`,
      },
      totalElectors: {
        label: 'Totale Elettori',
        value:
          Year.Y2024 === year
            ? formatNumber(
                getAffluenzaByLivelloEleT(
                  dynamicTurnout,
                  level,
                  result.int.cod_reg,
                  result.int.cod_prov,
                  result.int.cod_com
                )
              )
            : formatNumber(result.int.ele_t),
      },
      totalVoters: {
        label: 'Totale Votanti',
        value:
          Year.Y2024 === year
            ? formatNumber(
                getAffluenzaByLivelloVoti(
                  dynamicTurnout,
                  level,
                  result.int.cod_reg,
                  result.int.cod_prov,
                  result.int.cod_com
                )
              )
            : formatNumber(result.int.vot_t),
      },
      blankCards: {
        label: 'Schede Bianche',
        value: formatNumber(result.int.sk_bianche),
      },
      contestedCards: {
        label: 'Schede Contestate',
        value:
          Year.Y2019 === year || Year.Y2022 === year
            ? '-1'
            : formatNumber(result.int.sk_contestate),
      },
      nullCards: {
        label:
          Year.Y2019 === year || Year.Y2022 === year
            ? 'Schede Nulle o Contestate'
            : 'Schede Nulle',
        value: formatNumber(result.int.sk_nulle),
      },
      validVotes: {
        //"Totali voti validi" (=Votanti - (Bianche + Nulle))
        label: 'Totali voti validi',
        value: formatNumber(
          +(Year.Y2024 === year
            ? getAffluenzaByLivelloVoti(
                dynamicTurnout,
                level,
                result.int.cod_reg,
                result.int.cod_prov,
                result.int.cod_com
              )
            : result.int.vot_t) -
            result.int.sk_bianche -
            result.int.sk_nulle
        ),
      },
    },
  };
};

/**
 * Converte il risultato ricevuto dall'api in un modello utilizzabile sulla mappa
 * @param result Risultato API
 * @param year  Filtro Anno
 * @param region Filtro per Regione
 * @param level Livello da impostare sull mappa
 */
export const parseResultToMapLayer = (
  result: ResultAPI,
  year: Year,
  region: Region,
  level: DataLevel
) => {
  const { variable } = getLevelSettings(level);

  const winningCandidate = result.cand?.sort((a, b) => {
    const aPerc = parsePercentage(a.perc_uni || a.perc || '0');
    const bPerc = parsePercentage(b.perc_uni || b.perc || '0');
    return bPerc - aPerc;
  })[0];

  const votes = winningCandidate?.voti_uni || winningCandidate?.voti || 0;
  const perc = winningCandidate?.perc_uni || winningCandidate?.perc || null;

  let color = '#D9D9D9'; // default se voti 0 o perc null

  if (region !== Region.ALL && winningCandidate === undefined) {
    // console.log('check', result);
  } else if (region === Region.ALL || (votes > 0 && perc !== null)) {
    // console.log("%c PRE - GETCOALITIONCOLOR", "color:" + color + ";font-weight:bold;font-size: 30px", year, region, level, { pos: result.pos } );

    if (region === Region.ALL) {
      color = getCoalitionColor(year, region, level, { pos: result.pos });
    } else {
      color = getCoalitionColor(year, region, level, winningCandidate);
    }
    // console.log( 'provaprova', year, region, level, winningCandidate, result.pos, color )
  }

  let idFromVariable: string = result.int[variable] as string; // default
  if (level === DataLevel.PROVINCIA) {
    idFromVariable = convertIdProvince(
      `${result.int.cod_reg}`,
      `${result.int.cod_prov}`
    );
  } else if (level === DataLevel.COMUNE) {
    idFromVariable = convertIdComune(
      `${result.int.cod_reg}`,
      `${result.int.cod_prov}`,
      `${result.int.cod_com}`
    );
  }

  return {
    fillColor: color,
    opacity: 1, // full color of the winning coalition // parsePercentage(winningCandidate?.perc) / 100,
    layerId: idFromVariable,
    coordinates: [],
  };
};

const fetchResults = async (
  abort: boolean,
  year: Year,
  region: Region,
  level: DataLevel,
  codes?: ResultCodes
): Promise<ResultAPI | ResultAPI[] | null> => {
  // console.log( "FETCH RESULT ",
  //   abort,
  //   year,
  //   region,
  //   level,
  //   codes
  // )
  let yearDirectory: string;
  switch (year) {
    case Year.Y2018:
      yearDirectory = '2018/';
      break;
    case Year.Y2019:
      yearDirectory = '2019/';
      break;
    case Year.Y2022:
      yearDirectory = '2022/';
      break;
    case Year.Y2023:
      yearDirectory = '2023/';
      break;
    case Year.Y2024:
    default:
      yearDirectory = '2024/';
      break;
  }

  let url = '';
  const typeDirectory = '';
  let regionDirectory;

  switch (region) {
    case Region.LOMBARDIA:
      regionDirectory = '03';
      break;
    case Region.LAZIO:
      regionDirectory = '12';
      break;
    case Region.ABRUZZO:
      regionDirectory = '13';
      break;
  }

  if (region === Region.ALL || region === Region.ABRUZZO) {
    if (year === Year.Y2019 || year === Year.Y2022) {
      url = `${API_URL}${yearDirectory}${
        year === Year.Y2019 ? '06' : '07'
      }${regionDirectory}`;
      let urlLevel = 'reg';

      if (level === DataLevel.COMUNE) {
        urlLevel = 'com';
      } else if (level === DataLevel.PROVINCIA) {
        urlLevel = 'prov';
      } else if (level === DataLevel.REGIONE) {
        urlLevel = 'reg';
      }

      url += `${urlLevel}_Abruzzo${convertYearEnumToStr(year)}.json`;
    } else {
      url = `${API_URL}${yearDirectory}${typeDirectory}07${regionDirectory}`;

      if (level === DataLevel.PROVINCIA) {
        url += 'prov';
      }
      if (level === DataLevel.COMUNE) {
        url += 'comuni_all_4';
      }

      url += '.json';
    }
  }

  try {
    if (abort) {
      fetchController.abort();
      fetchController = new AbortController();
    }

    let res: ResultAPI | ResultAPI[] = await (
      await fetch(url, {
        signal: fetchController.signal,
      })
    )
      .json()
      .catch(_ => {
        return null;
      });

    if (level === DataLevel.COMUNE) {
      if (
        codes === undefined ||
        (codes !== undefined && codes.codProv === undefined)
      )
        return res;
      else {
        const codProv = RIGHT(codes!.codProv!);
        return (res as any[]).filter(
          (comune: any) => comune.int.cod_prov == codProv
        );
      }
    }

    return res;
  } catch {
    return null;
  }
};

export const getDynamicTurnout = async () => {
  try {
    fetchDynamicTurnoutController.abort();
    fetchDynamicTurnoutController = new AbortController();

    const dynamicTurnout = await (
      await fetch(`${API_URL}2024/votanti/affluenza.json`, {
        signal: fetchDynamicTurnoutController.signal,
      })
    ).json();

    return dynamicTurnout;
  } catch (e) {
    return [];
  }
};

/**
 * Effettua una chiamata al servizio e modella il risultato per essere utilizzato sulla mappa
 * @param abort
 * @param year
 * @param region
 * @param level
 * @param dynamicTurnout
 * @param codes
 */
export const getResults = async (
  abort: boolean,
  year: Year,
  region: Region,
  level: DataLevel,
  dynamicTurnout: DynamicTurnout[],
  codes?: ResultCodes
): Promise<Result[]> => {
  try {
    let data = await fetchResults(abort, year, region, level, codes);

    data = !data ? [] : Array.isArray(data) ? data : [data];

    return data.map((result: ResultAPI) => ({
      ...parseResult(result, year, region, level, dynamicTurnout),
      mapLayer: parseResultToMapLayer(result, year, region, level),
    }));
  } catch (e) {
    console.error('error fetching results', e);
    return [];
  }
};

export const getAutocompleteOptions = () => {
  const data = AdminDivisionsCamera as AutocompleteOption[];

  return data
    .reduce((acc: AutocompleteOption[], el) => {
      if (
        acc.some(
          option =>
            option.cod_comune === el.cod_comune &&
            option.cod_provincia === el.cod_provincia
        )
        // Limit data to Lombardia and Lazio
        // TODO DA ELIMINARE || (el.cod_regione !== '3' && el.cod_regione !== '12' && el.cod_regione !== '13')
      ) {
        return acc;
      }

      return [...acc, el];
    }, [])
    .sort((a, b) => {
      // should be:
      // a.cod_provincia.localeCompare(b.cod_provincia);

      return a.nome_provincia.localeCompare(b.nome_provincia);
    });
};

export const getLastVersion = async () => {
  return await (
    await fetch(
      'https://2024-real-time-data.s3.eu-central-1.amazonaws.com/2024/last_version.json' //TODO DA CAMBIARE CON ENV
    )
  ).json();
};

/**
 * Restituisce le informazioni di tutti i comuni di una determinata provincia
 * @param regionCode
 * @param provinceCode
 */
export const getMunicipalities = async (
  regionCode: string,
  provinceCode: string,
  year: Year,
  level: DataLevel,
  dynamicTurnout: DynamicTurnout[]
) => {
  // console.log(regionCode, provinceCode);
  if (!regionCode && !provinceCode) return null;

  const data = await (
    await fetch(
      `${API_URL}${convertYearEnumToStr(year)}/07${RIGHT(regionCode)}${RIGHT(
        provinceCode,
        '0',
        3
      )}.json`
    )
  ).json();

  return data.map((result: ResultAPI) => ({
    ...parseResult(result, year, +regionCode as Region, level, dynamicTurnout),
    mapLayer: parseResultToMapLayer(result, year, +regionCode as Region, level),
  }));

  //https://2023-real-time-data.s3.eu-central-1.amazonaws.com/2022/0703057.json
};

/**
 * UTILS
 * @param str
 * @param fillChar
 * @param num
 * @constructor
 */
const RIGHT = (
  str: string | number,
  fillChar: string = '0',
  num: number = 2
) => {
  let fillStr = '';

  for (let i = 0; i <= num; i++) {
    fillStr += fillChar;
  }

  fillStr += (str || '') + '';

  return fillStr.substring(fillStr.length - num, fillStr.length);
};

/**
 *
 */
const convertYearEnumToStr = (year: Year) => {
  switch (year) {
    case Year.Y2018:
      return '2018';
    case Year.Y2019:
      return '2019';
    case Year.Y2022:
      return '2022';
    case Year.Y2023:
      return '2023';
    case Year.Y2024:
      return '2024';
  }
};
